import { SALE_API_URL } from "../../../constants";
import { apiSlice } from "../../../services";
import { IApiRes } from "../../../types/interfaces";

// Request interfaces
export interface IGetStoreKiosksReq {
    store_id: string;
    page?: number;
    limit?: number;
}

export interface ICreateKioskReq {
    store_id: string;
    name: string;
    description?: string;
}

export interface IUpdateKioskReq {
    kiosk_id: string;
    name?: string;
    description?: string;
}

export interface IDeleteKioskReq {
    store_id: string;
    ids: string[];
}

export interface ICreateTmpLoginCodeReq {
    kiosk_id: string;
}

export interface IGetKioskSessionsReq {
    kiosk_id: string;
    page?: number;
    limit?: number;
}

// Response interfaces
export interface IKiosk {
    id: string;
    store_id: string;
    name: string;
    description: string;
    created_at: string;
}

export interface IGetStoreKiosksRes extends IApiRes {
    data: {
        total_count: number;
        result: IKiosk[];
    };
}

export interface ICreateTmpLoginCodeRes extends IApiRes {
    data: {
        login_code: string;
    };
}

export interface IKioskSession {
    id: string;
    kiosk_id: string;
    latest_used_time: string;
    created_at: string;
    expired_at: string;
}

export interface IGetKioskSessionsRes extends IApiRes {
    data: {
        total_count: number;
        result: IKioskSession[];
    };
}

export const kioskApiSlice = apiSlice.injectEndpoints({
    endpoints: (builder) => ({
        // Get store kiosks
        getStoreKiosks: builder.query<IGetStoreKiosksRes, IGetStoreKiosksReq>({
            query: (params) => {
                const { store_id, page = 1, limit = 10 } = params;
                return {
                    url: `${SALE_API_URL}/stores/${store_id}/kiosks`,
                    method: "GET",
                    params: { page, limit },
                };
            },
            providesTags: ["SalesItems"],
        }),

        // Create kiosk
        createKiosk: builder.mutation<IApiRes, ICreateKioskReq>({
            query: (params) => {
                const { store_id, ...body } = params;
                return {
                    url: `${SALE_API_URL}/stores/${store_id}/kiosks`,
                    method: "POST",
                    body,
                };
            },
            invalidatesTags: ["SalesItems"],
        }),

        // Update kiosk
        updateKiosk: builder.mutation<IApiRes, IUpdateKioskReq>({
            query: (params) => {
                const { kiosk_id, ...body } = params;
                return {
                    url: `${SALE_API_URL}/kiosks/${kiosk_id}`,
                    method: "PATCH",
                    body,
                };
            },
            invalidatesTags: ["SalesItems"],
        }),

        // Delete kiosk
        deleteKiosk: builder.mutation<IApiRes, IDeleteKioskReq>({
            query: (params) => {
                const { store_id, ids } = params;
                return {
                    url: `${SALE_API_URL}/stores/${store_id}/kiosks`,
                    method: "DELETE",
                    body: { ids },
                };
            },
            invalidatesTags: ["SalesItems"],
        }),

        // Create temporary login code
        createTmpLoginCode: builder.mutation<ICreateTmpLoginCodeRes, ICreateTmpLoginCodeReq>({
            query: (params) => ({
                url: `${SALE_API_URL}/kiosks/${params.kiosk_id}/tmp_login_code`,
                method: "POST",
            }),
        }),

        // Get kiosk sessions
        getKioskSessions: builder.query<IGetKioskSessionsRes, IGetKioskSessionsReq>({
            query: (params) => {
                const { kiosk_id, page = 1, limit = 10 } = params;
                return {
                    url: `${SALE_API_URL}/kiosks/${kiosk_id}/sessions`,
                    method: "GET",
                    params: { page, limit },
                };
            },
            providesTags: ["SalesItems"],
        }),
    }),
});

export const {
    useGetStoreKiosksQuery,
    useCreateKioskMutation,
    useUpdateKioskMutation,
    useDeleteKioskMutation,
    useCreateTmpLoginCodeMutation,
    useGetKioskSessionsQuery,
} = kioskApiSlice;
